import { create } from "zustand";

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  resolve: ((value: boolean) => void) | null;
}

interface DialogStore {
  toasts: Toast[];
  addToast: (message: string, type?: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;

  confirm: ConfirmState;
  showConfirm: (options: {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
  }) => Promise<boolean>;
  resolveConfirm: (value: boolean) => void;
}

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  error: 6000,
  warning: 6000,
};

const MAX_TOASTS = 5;

export const useDialogStore = create<DialogStore>((set, get) => ({
  toasts: [],

  addToast: (message, type = "info", duration) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toast: Toast = {
      id,
      message,
      type,
      duration: duration ?? DEFAULT_DURATIONS[type],
    };

    set((state) => {
      const updated = [...state.toasts, toast];
      if (updated.length > MAX_TOASTS) {
        return { toasts: updated.slice(updated.length - MAX_TOASTS) };
      }
      return { toasts: updated };
    });
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  confirm: {
    isOpen: false,
    title: "Confirm",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    resolve: null,
  },

  showConfirm: (options) => {
    return new Promise<boolean>((resolve) => {
      set({
        confirm: {
          isOpen: true,
          title: options.title ?? "Confirm",
          message: options.message,
          confirmText: options.confirmText ?? "Confirm",
          cancelText: options.cancelText ?? "Cancel",
          resolve,
        },
      });
    });
  },

  resolveConfirm: (value) => {
    const { confirm } = get();
    confirm.resolve?.(value);
    set({
      confirm: {
        isOpen: false,
        title: "Confirm",
        message: "",
        confirmText: "Confirm",
        cancelText: "Cancel",
        resolve: null,
      },
    });
  },
}));

// Convenience helpers — callable from anywhere (including non-React code)
export const showToast = (
  message: string,
  type?: ToastType,
  duration?: number
) => {
  useDialogStore.getState().addToast(message, type, duration);
};

export const showConfirm = (
  message: string,
  options?: {
    title?: string;
    confirmText?: string;
    cancelText?: string;
  }
) => {
  return useDialogStore.getState().showConfirm({ message, ...options });
};
